"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect, useMemo } from "react";
import Loader from "../Loader";

type Category = {
  id: string;
  name: string;
  image?: string;
  discount?: number;
  smDesc?: string;
  badge?: string;
  color?: string;
  createdAt?: string; // 👈 important for sorting
};

type ShopCategoryProps = {
  spacing?: string;
  categories?: Category[];
};

const ShopCategory = ({ spacing = "100px 0", categories = [] }: ShopCategoryProps) => {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ FRONTEND SORTING (ASC)
  const sortedCategories = useMemo(() => {
    return [...categories].sort(
      (a, b) =>
        new Date(a.createdAt || "").getTime() -
        new Date(b.createdAt || "").getTime()
    );
  }, [categories]);

  if (!sortedCategories || sortedCategories.length === 0) return null;

  return (
    <>
      {loading && <Loader />}

      <section
        style={{
          padding: isMobile ? "40px 0" : spacing,
          backgroundColor: "#fff",
          position: "relative",
          overflow: "hidden",
          marginTop: isMobile ? "-80px" : "-50px",
        }}
      >
        {/* BACKGROUND CIRCLE */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: -200,
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(0,0,0,0.02) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: isMobile ? "0 16px" : "0 60px",
            marginBottom: isMobile ? "0px" : "70px",
          }}
        >
          {/* HEADER */}
          <div style={{ textAlign: "center", marginBottom: isMobile ? "10px" : "70px" }}>
            <p style={{ fontSize: "14px", fontWeight: 700, letterSpacing: "2px", color: "#888" }}>
              Explore By Category
            </p>

            <h2
              style={{
                fontSize: isMobile ? "30px" : "56px",
                fontWeight: 900,
                color: "#111",
                marginBottom: "12px",
              }}
            >
              Shop By Category
            </h2>

            <p
              style={{
                fontSize: isMobile ? "14px" : "18px",
                color: "#555",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Browse through our curated fashion categories – find your perfect style.
            </p>
          </div>

          {/* GRID */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
              gap: isMobile ? "12px" : "32px",
              marginBottom: isMobile ? "30px" : "60px",
            }}
          >
            {sortedCategories.map((item) => (
              <Link
                key={item.id}
                href={`/shop?category=${item.id}`}
                onClick={() => setLoading(true)}
                onMouseEnter={() => setHoveredCard(item.id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div
                  style={{
                    background: "#f9f9f9",
                    borderRadius: "20px",
                    padding: isMobile ? "16px 12px" : "40px 32px",
                    border: "1px solid #eee",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    boxShadow:
                      hoveredCard === item.id
                        ? "0 20px 50px rgba(0,0,0,0.12)"
                        : "0 4px 15px rgba(0,0,0,0.05)",
                    transform: hoveredCard === item.id ? "translateY(-8px)" : "none",
                    transition: "0.4s",
                  }}
                >
                  {/* BADGE */}
                  {item.badge && (
                    <div
                      style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        padding: "6px 14px",
                        background: item.color || "#ff6b6b",
                        color: "#fff",
                        borderRadius: "50px",
                        fontSize: "11px",
                        fontWeight: 800,
                      }}
                    >
                      {item.badge}
                    </div>
                  )}

                  {/* IMAGE */}
                  <div
                    style={{
                      width: "100%",
                      height: isMobile ? "80px" : "140px",
                      borderRadius: "12px",
                      overflow: "hidden",
                      background: "#eee",
                      marginBottom: "16px",
                      position: "relative",
                    }}
                  >
                    {item.image && (
                      <Image src={item.image} alt={item.name} fill style={{ objectFit: "cover" }} />
                    )}
                  </div>

                  {/* TEXT */}
                  <h3 style={{ fontSize: isMobile ? "14px" : "22px", fontWeight: 900 }}>
                    {item.name}
                  </h3>

                  <p
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      fontWeight: 600,
                      color: "#666",
                      marginBottom: "12px",
                    }}
                  >
                    {item.smDesc || "Explore collection"}
                  </p>

                  {/* CTA */}
                  <div
                    style={{
                      marginTop: "auto",
                      color: item.color || "#ff6b6b",
                      fontSize: isMobile ? "14px" : "20px",
                      fontWeight: 800,
                    }}
                  >
                    Shop Now →
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* FOOTER CTA */}
          <div style={{ textAlign: "center", borderTop: "1px solid #eee", paddingTop: "30px" }}>
            <p style={{ color: "#424242", marginBottom: "20px" }}>
              Can't find what you're looking for?
            </p>

            <Link
              href="/shop"
              style={{
                padding: "14px 40px",
                background: "#000",
                color: "#fff",
                borderRadius: "8px",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ShopCategory;
